// src/pages/DashboardPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Link, useOutletContext } from "react-router-dom"; 
import {
  UserGroupIcon,
  PlusCircleIcon,
  ChartBarIcon,
  ClockIcon,
  BellIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  QuestionMarkCircleIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import Button from "../components/common/Button";
import { useWallet } from "../contexts/WalletContext";
import {
  getDailyAgentActivityChartData,
  getDashboardStats,
  getRecentActivities,
} from "../services/dashboardService"; 
import Spinner from "../components/common/Spinner";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";


const WidgetCard = ({
  title,
  children,
  icon,
  className = "",
  colSpan = "sm:col-span-1",
  cardHeight = "auto",
  titleClassName = "",
}) => {
  const IconComponent = icon;
  return (
    <div
      className={`
        bg-slate-800/70 backdrop-blur-xl 
        border border-slate-700/60 
        rounded-2xl shadow-xl 
        p-5 sm:p-6 
        ${className} ${colSpan}
        flex flex-col
      `}
      style={{ minHeight: cardHeight === "auto" ? "auto" : cardHeight }} 
    >
      {title && (
        <div
          className={`flex items-center text-slate-200 mb-4 ${titleClassName}`}
        >
          {IconComponent && (
            <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 mr-2.5 sm:mr-3 text-blue-400" />
          )}
          <h3 className="text-md sm:text-lg font-semibold">{title}</h3>
        </div>
      )}
      <div className="flex-grow flex flex-col justify-start">{children}</div>
    </div>
  );
};

const helpLinks = [
  {
    name: "Getting Started Guide",
    href: "/docs/getting-started", 
    icon: BookOpenIcon,
  },
  {
    name: "Frequently Asked Questions",
    href: "/faq", 
    icon: ChatBubbleLeftRightIcon,
  },
  {
    name: "Contact Support",
    href: "/contact-support", 
    icon: EnvelopeIcon,
  },
];

const formatChartData = (data) => {
  if (!Array.isArray(data)) return [];
  return data.map((item) => ({
    ...item,
    dayLabel: new Date(item.date).toLocaleDateString("en-US", {
      weekday: "short",
    }),
  }));
};

const DashboardPage = () => {
  const { currentAccount } = useWallet();
  const { openCreateAgentModalFromLayout } = useOutletContext() || {}; 

  const [dashboardStats, setDashboardStats] = useState({
    totalAgents: 0,
    activeAgents: 0,
  });
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState("");

  const [agentActivityData, setAgentActivityData] = useState([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [chartError, setChartError] = useState("");

  const [recentActivitiesData, setRecentActivitiesData] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [activitiesError, setActivitiesError] = useState("");

  const fetchDashboardStats = useCallback(async () => {
    if (currentAccount) {
      setIsLoadingStats(true);
      setStatsError("");
      try {
        const stats = await getDashboardStats(currentAccount);
        setDashboardStats(stats);
      } catch (err) {
        setStatsError(err.message || "Could not load dashboard stats.");
      } finally {
        setIsLoadingStats(false);
      }
    }
  }, [currentAccount]);

  // Fetch data chart
  const fetchChartData = useCallback(async () => {
    if (currentAccount) {
      setIsLoadingChart(true);
      setChartError("");
      try {
        const rawChartData = await getDailyAgentActivityChartData(
          currentAccount
        ); // Dari agentService
        setAgentActivityData(formatChartData(rawChartData));
      } catch (err) {
        setChartError(err.message || "Could not load chart data.");
      } finally {
        setIsLoadingChart(false);
      }
    } else {
      setAgentActivityData([]);
    }
  }, [currentAccount]);

  const fetchRecentActivities = useCallback(async () => {
    if (currentAccount) {
      setIsLoadingActivities(true);
      setActivitiesError("");
      try {
        const activities = await getRecentActivities(currentAccount, 5); 
        setRecentActivitiesData(activities);
      } catch (err) {
        setActivitiesError(err.message || "Could not load recent activities.");
      } finally {
        setIsLoadingActivities(false);
      }
    }
  }, [currentAccount]);

  useEffect(() => {
    if (currentAccount) {
      fetchDashboardStats();
      fetchChartData();
      fetchRecentActivities();
    }
  }, [
    currentAccount,
    fetchDashboardStats,
    fetchChartData,
    fetchRecentActivities,
  ]);

  const handleCreateAgentClick = () => {
    if (openCreateAgentModalFromLayout) {
      openCreateAgentModalFromLayout();
    } else {
      console.warn(
        "Create Agent Modal trigger function not found via context. Navigating to page instead (if route exists)."
      );
    }
  };

  return (
    <div className="animate-fadeIn space-y-6 sm:space-y-8">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-100 mb-2">
          Welcome to Your Dashboard
        </h1>
        <p className="text-md text-slate-400">
          Here's an overview of your AI agent activities and quick actions.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {" "}
        <WidgetCard
          title="My Agents Summary"
          icon={UserGroupIcon}
          className="lg:col-span-1"
        >
          {isLoadingStats ? (
            <div className="flex justify-center items-center h-24">
              <Spinner />
            </div>
          ) : statsError ? (
            <p className="text-red-400 text-sm">{statsError}</p>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-between items-baseline">
                <p className="text-slate-300 text-sm">Total Agents:</p>
                <p className="text-2xl font-bold text-blue-400">
                  {dashboardStats.totalAgents}
                </p>
              </div>
              <div className="flex justify-between items-baseline">
                <p className="text-slate-300 text-sm">Active Agents:</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {dashboardStats.activeAgents}
                </p>
              </div>
              <Link to="/my-agents">
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full mt-4 !text-slate-200 !bg-slate-700 hover:!bg-slate-600 !py-2"
                >
                  Manage My Agents
                </Button>
              </Link>
            </div>
          )}
        </WidgetCard>
        {/* Widget Quick Actions */}
        <WidgetCard
          title="Quick Actions"
          icon={PlusCircleIcon}
          className="lg:col-span-1"
        >
          <div className="space-y-3">
            <Button
              onClick={handleCreateAgentClick} 
              variant="primary"
              className="w-full bg-gradient-to-r from-blue-500 to-red-500 hover:from-blue-600 hover:to-red-600 border-transparent !py-2.5"
            >
              Create New Agent{" "}
            </Button>

            <Link to="/discover">
              <Button
                variant="ghost"
                className="w-full !text-blue-400 hover:!bg-blue-500/10 !py-2.5 mt-2"
              >
                Discover Agents
              </Button>
            </Link>
          </div>
        </WidgetCard>
        <WidgetCard
          title="Agent Runs (Last 7 Days)"
          icon={ChartBarIcon}
          className="md:col-span-2 lg:col-span-2"
          cardHeight="380px"
        >
          {" "}
          {isLoadingChart ? (
            <div className="flex justify-center items-center h-full">
              <Spinner />
              <p className="ml-2 text-slate-400 text-sm">Loading chart...</p>
            </div>
          ) : chartError ? (
            <div className="flex flex-col items-center justify-center h-full text-red-400">
              <ExclamationTriangleIcon className="h-8 w-8 mb-2" />
              <p>{chartError}</p>
            </div>
          ) : agentActivityData && agentActivityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={agentActivityData}
                margin={{ top: 100, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="dayLabel" stroke="#cbd5e1" />
                <YAxis stroke="#cbd5e1" />
                <Tooltip
                  contentStyle={{
                    background: "#1e293b",
                    border: "1px solid #334155",
                    color: "#f1f5f9",
                  }}
                  labelStyle={{ color: "#fbbf24" }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="runs"
                  stroke="#fbbf24"
                  strokeWidth={0.8}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-500">
              <InformationCircleIcon className="h-10 w-10 mb-2" />
              <p>No agent activity data for the last 7 days.</p>
            </div>
          )}
        </WidgetCard>
        <WidgetCard
          title="Recent Activities"
          icon={BellIcon}
          className="md:col-span-2 lg:col-span-2"
          cardHeight="380px"
        >
          {isLoadingActivities ? (
            <div className="flex justify-center items-center h-full">
              <Spinner />
              <p className="ml-2 text-slate-400 text-sm">
                Loading activities...
              </p>
            </div>
          ) : activitiesError ? (
            <p className="text-red-400 text-sm text-center">
              {activitiesError}
            </p>
          ) : recentActivitiesData.length > 0 ? (
            <ul className="space-y-3 max-h-[280px] overflow-y-auto custom-scrollbar pr-2 -mr-2">
              {" "}
              {recentActivitiesData.map((activity) => (
                <li
                  key={activity.id}
                  className={`p-3 rounded-lg text-sm flex items-start
                                                    ${
                                                      activity.isWarning
                                                        ? "bg-red-600/10 text-red-300 hover:bg-red-600/20"
                                                        : "bg-slate-700/60 text-slate-300 hover:bg-slate-700/80"
                                                    } transition-colors`}
                >
                  <ClockIcon
                    className={`h-4 w-4 mr-2.5 mt-0.5 flex-shrink-0 ${
                      activity.isWarning ? "text-red-400" : "text-slate-400"
                    }`}
                  />
                  <div className="flex-1">
                    <p className="leading-tight">{activity.text}</p>
                    <p
                      className={`text-xs mt-1 ${
                        activity.isWarning
                          ? "text-red-400/80"
                          : "text-slate-500"
                      }`}
                    >
                      {activity.time}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-500">
              <InformationCircleIcon className="h-10 w-10 mb-2" />
              <p>No recent activity.</p>
            </div>
          )}
        </WidgetCard>
        <WidgetCard
          title="Help & Support"
          icon={QuestionMarkCircleIcon}
          className="md:col-span-1 lg:col-span-2"
        >
          <div className="space-y-2">
            {helpLinks.map((linkItem) => {
              const LinkIcon = linkItem.icon;
              return (
                <Link
                  key={linkItem.name}
                  to={linkItem.href}
                  className="flex items-center justify-between p-3 rounded-lg text-slate-300 
                                           hover:bg-slate-700/70 hover:text-blue-300 
                                           transition-all duration-200 ease-in-out group"
                >
                  <div className="flex items-center">
                    <LinkIcon className="h-5 w-5 mr-3 text-slate-400 group-hover:text-blue-400 transition-colors" />
                    <span className="text-sm font-medium">{linkItem.name}</span>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 text-slate-500 group-hover:text-blue-400 transition-colors" />
                </Link>
              );
            })}
          </div>
        </WidgetCard>
      </div>
    </div>
  );
};

export default DashboardPage;
